import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notificationReceived } from '@comeon/analytics';

import { getCookies, clearCookie } from '~/store/cookies.js';

import {
	clearNotifications,
	markReceived,
	updateMyNotificationsList,
	addNotificationPopupToQueue,
	updateBluebarNotification
} from './ducks/my-notifications.js';

import { selectIsAboveAgeLimit, selectDateOfBirth } from '~/components/account/ducks/index';
import enabledCategoriesSelector from '~/components/account/my-notifications/utils/enabled-categories-selector';
import { allCategories } from '~/components/account/my-notifications/my-notifications-utils';

const trackNotificationReceived = (id, title, recurringNotificationId) => {
	const loggedInTime = getCookies('loggedInTime'); // Cookies.get('loggedInTime');
	const secondsSinceLogin = (Date.now() - loggedInTime) / 1000;
	notificationReceived(
		`${recurringNotificationId ? `r${recurringNotificationId} - ` : ''}${id} - ${title} - ${secondsSinceLogin}`
	);
};

const ServerNotificationListener = () => {
	const dispatch = useDispatch();

	const [eventStream, setEventStream] = useState(null);

	const authenticated = useSelector(state => state.authentication.authenticated);

	const myNotificationsBlueBarEnabled = useSelector(state => state.brand.myNotificationsBlueBarEnabled);
	const myNotificationsPopupEnabled = useSelector(state => state.brand.myNotificationsPopupEnabled);
	const enabledCategories = useSelector(enabledCategoriesSelector);
	const displayedCategories = useMemo(() => {
		const ret = [];
		enabledCategories.forEach((isEnabled, i) => {
			if (isEnabled) {
				ret.push(allCategories[i]);
			}
		});
		return ret;
	}, [enabledCategories]);
	const oldEnough = useSelector(selectIsAboveAgeLimit);
	const birth = useSelector(selectDateOfBirth);

	const bindEventHandlers = useCallback(
		eventSource => {
			// 1 no popup
			// 2 popup
			// 3 blue bar
			// 4 [One-OFF Notifications] no popup
			// 5 [One-OFF Notifications] popup
			// 6 [One-OFF Notifications] blue bar
			// 7 [Recurring Notifications] no popup
			// 8 [Recurring Notifications] popup
			// 9 [Recurring Notifications] blue bar
			// 10 [SoW Notification] no popup
			// 11 [SoW Notification] popup
			// 12 [SoW Notification] blue bar
			['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].forEach(eventType => {
				eventSource.addEventListener(eventType, updateEvent => {
					const parsedMessage = JSON.parse(updateEvent.data);
					let notificationCategory = 'INBOX_CATEGORY_ALL';

					if (parsedMessage?.data && typeof parsedMessage.data === 'string') {
						try {
							const notificationData = JSON.parse(parsedMessage.data);
							notificationCategory = notificationData.NotificationCategory;
						} catch (e) {
							console.log(e);
						}
					}

					let shouldBlockNotification = true;

					// Check for U24 and non-account category message
					// if the player is U24, only show/register notifications with
					// category === INBOX_CATEGORY_ACCOUNT
					if (!oldEnough) {
						if (notificationCategory === `INBOX_CATEGORY_ACCOUNT`) {
							shouldBlockNotification = false;
						}
					} else {
						shouldBlockNotification = false;
					}

					if (!displayedCategories.includes(notificationCategory)) {
						shouldBlockNotification = true;
					}
					// Special handling for U24 players
					// Only add `INBOX_CATEGORY_ACCOUNT` notifications to the list
					if (!shouldBlockNotification) {
						dispatch(updateMyNotificationsList([parsedMessage]));
					}

					if (!parsedMessage.readNotification) {
						switch (eventType) {
							// Normal Notifications
							case '1':
							case '4':
							case '7':
							case '10':
								if (!parsedMessage.receivedNotification && !shouldBlockNotification) {
									dispatch(markReceived(parsedMessage.id));
									trackNotificationReceived(
										parsedMessage.id,
										parsedMessage.title,
										parsedMessage.recurringNotificationId
									);
								}
								break;

							// Popup Notifications
							case '2':
							case '5':
							case '8':
							case '11':
								if (
									myNotificationsPopupEnabled &&
									!parsedMessage.receivedNotification &&
									!shouldBlockNotification
								) {
									dispatch(addNotificationPopupToQueue(parsedMessage.id));
								}
								break;

							// Bluebar Notifications
							case '3':
							case '6':
							case '9':
							case '12':
								if (
									myNotificationsBlueBarEnabled &&
									!parsedMessage.receivedNotification &&
									!shouldBlockNotification
								) {
									dispatch(updateBluebarNotification(parsedMessage));
								}
								if (!parsedMessage.receivedNotification && !shouldBlockNotification) {
									dispatch(markReceived(parsedMessage.id));
									trackNotificationReceived(
										parsedMessage.id,
										parsedMessage.title,
										parsedMessage.recurringNotificationId
									);
								}
								break;

							default:
								break;
						}
					} else if (parsedMessage.readNotification && !parsedMessage.receivedNotification) {
						// the already read notifications which were sent before this feature was released
						// should only be marked as received
						dispatch(markReceived(parsedMessage.id));
					}
				});
			});
		},
		[dispatch, myNotificationsBlueBarEnabled, myNotificationsPopupEnabled, oldEnough, displayedCategories]
	);

	const subscribeToEventStream = useCallback(() => {
		const source = new EventSource('/player-notifications/notification/');
		bindEventHandlers(source);
		setEventStream(source);
	}, [bindEventHandlers]);

	const unSubscribeFromEventStream = useCallback(() => {
		if (eventStream) {
			eventStream.close();
		}
		setEventStream(null);
		dispatch(clearNotifications());
		// reset the popup cooldown when user logs out
		// document.cookie = `popupCooldown=false;path='/'`;

		clearCookie('popupCooldown');
	}, [dispatch, eventStream]);

	useEffect(() => {
		if (authenticated && birth) {
			if (!eventStream) {
				subscribeToEventStream();
			}
		} else if (eventStream) {
			unSubscribeFromEventStream();
		}

		return () => {
			unSubscribeFromEventStream();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authenticated, birth]);

	return null;
};

export default ServerNotificationListener;
